import { useQuery } from "@apollo/client";
import { Award, Crown, Settings, Star, Trophy } from "lucide-react";
import { useFormContext, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { gql } from "@/gql";
import { useSalonStore } from "@/lib/store/salon";
import type { RewardsConfig } from "../hooks/use-rewards-config";

const GET_LOYALTY_TIERS = gql(`
  query GetLoyaltyTiers($franchiseId: ID!) {
    loyaltyTiers(franchiseId: $franchiseId) {
      id
      name
      description
      minPoints
      maxPoints
      priorityBooking
      color
      sortOrder
      isActive
    }
  }
`);

export function TierPreview() {
  const { control } = useFormContext<RewardsConfig>();
  const { t } = useTranslation("settings");
  const loyaltyProgramEnabled = useWatch({ control, name: "loyaltyProgramEnabled" });
  const enabled = useWatch({ control, name: "enabled" });

  const { currentSalon } = useSalonStore();
  const franchiseId = currentSalon?.franchiseId;

  const { data, loading, error } = useQuery(GET_LOYALTY_TIERS, {
    variables: { franchiseId: franchiseId ?? "" },
    skip: !franchiseId || !loyaltyProgramEnabled || !enabled,
  });

  if (!loyaltyProgramEnabled || !enabled) {
    return (
      <div className="p-4 bg-muted/50 rounded-lg text-center text-muted-foreground">
        {t("rewards.tierSystemDisabledMessage")}
      </div>
    );
  }

  if (loading) {
    return (
      <div className="space-y-3">
        {[...Array(3)].map((_, i) => (
          <Skeleton key={i} className="h-16 w-full" />
        ))}
      </div>
    );
  }

  if (error || !data?.loyaltyTiers?.length) {
    return (
      <div className="p-4 bg-muted/50 rounded-lg text-center text-muted-foreground">
        <p>{t("rewards.noTiersConfigured")}</p>
        <p className="text-sm mt-1 mb-3">{t("rewards.setUpTiers")}</p>
        <Button asChild size="sm">
          <Link to="/settings/rewards/tiers">
            <Settings className="h-4 w-4 mr-2" />
            {t("rewards.manageTiers")}
          </Link>
        </Button>
      </div>
    );
  }

  const tiers = data.loyaltyTiers
    .filter((tier) => tier.isActive)
    .sort((a, b) => a.sortOrder - b.sortOrder);

  return (
    <div className="space-y-4">
      <div className="space-y-3">
        {tiers.map((tier, index) => (
          <TierCard key={tier.id} tier={tier} index={index} />
        ))}
      </div>

      <div className="flex justify-center pt-2">
        <Button variant="outline" size="sm" asChild>
          <Link to="/settings/rewards/tiers">
            <Settings className="h-4 w-4 mr-2" />
            {t("rewards.manageTiers")}
          </Link>
        </Button>
      </div>
    </div>
  );
}

interface TierCardProps {
  tier: {
    id: string;
    name: string;
    description?: string | null;
    minPoints: number;
    maxPoints?: number | null;
    priorityBooking: boolean;
    color: string;
  };
  index: number;
}

function TierCard({ tier, index }: TierCardProps) {
  const { t } = useTranslation("settings");

  const getIcon = (index: number) => {
    const icons = [Star, Award, Crown, Trophy];
    const Icon = icons[Math.min(index, icons.length - 1)];
    return <Icon className="h-4 w-4" />;
  };

  const pointsRange = tier.maxPoints
    ? t("rewards.pointsRange", { min: tier.minPoints, max: tier.maxPoints })
    : t("rewards.pointsRangeUnlimited", { min: tier.minPoints });

  return (
    <Card className="border-2" style={{ borderColor: tier.color }}>
      <CardContent className="p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div
              className="flex items-center justify-center w-10 h-10 rounded-full text-white"
              style={{ backgroundColor: tier.color }}
            >
              {getIcon(index)}
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h4 className="font-semibold">{tier.name}</h4>
                <Badge variant="secondary" className="text-xs">
                  {pointsRange}
                </Badge>
              </div>
              {tier.description && (
                <p className="text-sm text-muted-foreground mt-1">{tier.description}</p>
              )}
            </div>
          </div>

          {/* Benefits (shown only if they exist) */}
          <div className="flex flex-col items-end gap-1 text-xs text-muted-foreground">
            {tier.priorityBooking && <span>{t("rewards.priorityBooking")}</span>}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
